/**
 * Taskforce Spearhead — 8-Week 4-Sector Assault
 *
 * Purpose-built for Operation Spearhead: secure all four sectors against
 * EREBUS. The Spearhead engine (lib/spearheadEngine.ts) classifies every
 * logged exercise into one of four domains and victory requires ALL FOUR
 * sectors at ≥80% control — so a lopsided program can never win. This
 * program is balanced by design: every sector is trained 3–5×/week.
 *
 *   ALPHA  (Industrial / strength)  = barbell tonnage  → squat, bench, DL, press, row, KB swing
 *   BRAVO  (Transport / endurance)  = meters/time      → run, row, bike, stair, walk
 *   CHARLIE(Elevated / bodyweight)  = reps             → pull-up, push-up, dip, burpee, plank
 *   DELTA  (Logistics / loaded)     = distance × load  → ruck, farmer/suitcase carry, sled
 *
 * Each day has a PRIMARY sector but deliberately touches 2–3 others so the
 * weekly contribution stays even. Six training days also keep the Spearhead
 * consistency index high (victory also needs consistency ≥70% + no 5-day gaps).
 *
 * Loading is 'progressive' (ML-driven) for the barbell work — actual logged
 * tonnage is what feeds Sector Alpha, so there's nothing to hard-code.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata } from '../types/program';
import type { PeriodizationPhaseConfig } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'taskforce-spearhead-8week',
  name: 'Taskforce Spearhead',
  description: 'The dedicated Operation Spearhead program — a six-day hybrid that pushes all four sectors at once: barbell strength, conditioning engine, bodyweight volume, and loaded carries. Balanced by design so no sector falls behind and every session moves the map.',
  level: 'operator',
  duration: 8,
  daysPerWeek: 6,
  goals: ['tactical-performance', 'work-capacity', 'strength', 'endurance'],
  difficulty: 'advanced',
  emphasis: 'Balanced four-domain hybrid: strength, engine, bodyweight, and loaded movement in every training week.',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
  // Strength + real aerobic base + ruck. ~120 min Z2/wk (aerobic day 40 +
  // two 12-min gym finishers + the steady ruck) keeps Sector Bravo fed
  // without competing with the barbell work.
  weeklyZ2Target: 120,
  assessmentFormat: 'integrated',
  // Full gym (barbell, sled, KB, bike/rower, pull-up + dip) for the 4 gym
  // days; an outdoor route (or treadmill) for the ruck + aerobic days.
  requiredEquipment: 'full-gym',
};

// Light block periodization for the timeline UI + intensity ramp. Phases carry
// no trainingParameters (repRange/setsMultiplier), so the rep-scaling path is
// never triggered — 'progressive' ML owns the actual loading.
/**
 * NO DELOAD PHASE — deliberate, confirmed 2026-08-11.
 *
 * Ten other shipped programs author a `type: 'deload'` phase and the
 * generator now honours them (it previously read a phantom `program.schedule`
 * field and every scheduled deload was dead). Spearhead intentionally has
 * none: it is a fixed-length challenge block, not an open-ended training
 * cycle, and the streak/adherence mechanic is the point.
 *
 * Do NOT "fix" this by adding a deload phase — an audit will flag its absence
 * as an inconsistency, and it is not one.
 *
 * Fatigue is still covered: the reactive backstop in lib/recoveryDebt.ts
 * (>= 4 consecutive sessions averaging RPE >= 8.0 within the last 10) fires
 * independently of any program schedule and cuts volume 50% / intensity 70%.
 */
const periodizationPhases: PeriodizationPhaseConfig[] = [
  {
    name: 'Accumulation', type: 'accumulation', weeks: [1, 2, 3],
    focus: 'Build volume across all four sectors',
    intensityMultiplier: 0.95, volumeMultiplier: 1.1,
    progression: { type: 'linear', startIntensity: 0.70, endIntensity: 0.78, weeklyIncrease: 0.04 },
  },
  {
    name: 'Intensification', type: 'intensification', weeks: [4, 5, 6],
    focus: 'Raise intensity, hold sector balance',
    intensityMultiplier: 1.05, volumeMultiplier: 1.0,
    progression: { type: 'linear', startIntensity: 0.78, endIntensity: 0.88, weeklyIncrease: 0.033 },
  },
  {
    name: 'Realization', type: 'realization', weeks: [7],
    focus: 'Peak output across sectors',
    intensityMultiplier: 1.1, volumeMultiplier: 0.9,
    progression: { type: 'step', startIntensity: 0.88, endIntensity: 0.92, weeklyIncrease: 0.04 },
  },
  {
    name: 'Deload', type: 'deload', weeks: [8],
    focus: 'Recover and consolidate territory',
    intensityMultiplier: 0.7, volumeMultiplier: 0.6,
    progression: { type: 'linear', startIntensity: 0.65, endIntensity: 0.65, weeklyIncrease: 0 },
  },
];

// ============================================================================
// PREP / RECOVERY — pattern-based, short, injury-augmentable (see the sweep)
// ============================================================================

const lowerPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
    { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before squatting/hinging' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Wake the posterior chain' },
    { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for squat depth' },
    { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap prime before the pull-ups + carries' },
  ],
};

const upperPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Rower — warm the upper back' },
    { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap retraction + rear delt' },
    { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before pressing' },
    { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Serratus + scap control for pressing + pull-ups' },
    { name: 'T-Spine Extension', sets: 1, reps: '8', format: 'prep', details: 'Thoracic mobility for press + row' },
  ],
};

const enginePrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Ease in — the run itself finishes the warm-up' },
    { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips' },
    { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Open hips + t-spine' },
    { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before the miles' },
  ],
};

const ruckPrep = {
  name: 'Ruck Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk unloaded walk before loading' },
    { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for a loaded gait' },
    { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before the march' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the load with the posterior chain, not the low back' },
  ],
};

// DAY 5 — front squat + push press + deadlift + pull-up + sled.
// Day 5 previously reused `lowerPrep`, which has no shoulder work at all —
// the athlete went straight into a heavy PUSH PRESS off a squat warm-up.
const powerPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Raise core temp' },
    { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Front squat depth is bought with dorsiflexion' },
    { name: 'Wrist Circles', sets: 1, reps: '10/direction', format: 'prep', details: 'The front rack asks a lot of the wrists' },
    { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the push press' },
    { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Scap control for the overhead lockout' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior chain before the deadlift' },
  ],
};

// DAY 6 — grinder circuit: swings, burpees, farmer carry, bike.
// Had an inline generic prep with no wrist or grip work, and a recovery block
// copied verbatim from `lowerRecovery` on a day containing no squatting.
const grinderPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Ease in — this one gets hard fast' },
    { name: 'Inchworm', sets: 1, reps: '5', format: 'prep', details: 'Hamstrings + wrists + shoulders, the whole burpee in one drill' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Hinge prime before the swings' },
    { name: 'Wrist Circles', sets: 1, reps: '10/direction', format: 'prep', details: 'Burpees put your bodyweight through the wrists, repeatedly' },
    { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap prime before the carries' },
  ],
};

const lowerRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after squatting' },
    { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the grip after carries' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
  ],
};

const upperRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after pressing' },
    { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + lats after pull-ups' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
  ],
};

const engineRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Hamstrings take the brunt of running' },
    { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate the effort' },
  ],
};

const ruckRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee — the ruck hammers calves' },
    { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after loaded walking' },
    { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose / cat-cow — decompress the spine' },
  ],
};

const powerRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the push press' },
    { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors after front squats and pulls' },
    { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress after deadlifts, and flush the lats after pull-ups' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
  ],
};

// The week's only max-heart-rate session, so it earns the longest
// down-regulation rather than the standard two minutes.
const grinderRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Forearm Stretch', duration: '45s/side', format: 'recovery', details: 'Grip took the swings and the carries' },
    { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress and flush the forearms' },
    { name: 'Child\'s Pose', duration: '1 min', format: 'recovery', details: 'Spine after the swings and hanging leg raises' },
    { name: '90/90 Breathing', duration: '3 min', format: 'recovery', details: 'Longest of the week — this is the day you actually redlined' },
  ],
};

// ============================================================================
// TRAINING WEEK — every sector hit, balanced across 6 days
// ============================================================================

// DAY 1 — Lower Strength · Gym (INDOOR) · ALPHA + CHARLIE + DELTA + BRAVO
const trainingDay1: WorkoutTemplateDay = {
  name: 'Lower Strength + Engine',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    lowerPrep,
    {
      name: 'Strength',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 5, reps: 5, intensity: 'progressive', rest: '3 min', details: 'ALPHA — heavy tonnage, full depth' },
        { name: 'Romanian Deadlift', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'ALPHA — hinge, stretch the hamstrings' },
      ],
    },
    {
      name: 'Bodyweight',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 4, reps: 6, intensity: 'progressive', rest: '90s', details: 'CHARLIE — full hang to chin over bar; add weight when 6 is easy' },
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', intensity: 'bodyweight', rest: '60s', details: 'CHARLIE — chest to deck' },
      ],
    },
    {
      name: 'Loaded Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 4, reps: '40m', intensity: 'progressive', rest: '90s', details: 'DELTA — heavy, tall posture' },
      ],
    },
    {
      name: 'Z2 Engine',
      type: 'conditioning',
      exercises: [
        { name: 'Assault Bike', sets: 1, duration: '12 min', format: 'cardio', intensity: 'Z2', details: 'BRAVO — easy conversational pace, nose-breathing. Bike or rower.' },
      ],
    },
    lowerRecovery,
  ],
};

// DAY 2 — Aerobic Base · Outdoor or treadmill · primary BRAVO (+ CHARLIE).
// The week's easy day: steady Z2 builds the engine, feeds Sector Bravo, and
// doubles as active recovery between the strength days.
const trainingDay2: WorkoutTemplateDay = {
  name: 'Aerobic Base',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    enginePrep,
    {
      name: 'Z2 Aerobic',
      type: 'conditioning',
      exercises: [
        { name: 'Run', sets: 1, duration: '40 min', format: 'cardio', intensity: 'Z2', details: 'BRAVO — steady conversational pace (can hold a sentence, nose-breathing). Outside or treadmill; rower/bike if running is limited.' },
      ],
    },
    {
      // Superset, NOT circuit: the circuit adapter parses a per-round rep count
      // out of "N×M" only, so an AMRAP member renders blank and a timed hold
      // ("3 × 45s") renders as "45 reps". The standard superset path handles
      // both correctly. Two-move core finishers are a superset anyway.
      name: 'Core Finisher',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', format: 'bodyweight', rest: '45s', details: 'CHARLIE — controlled tempo, bodyweight (do these anywhere)' },
        { name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight', rest: '45s', details: 'CHARLIE — braced, no sag' },
      ],
    },
    engineRecovery,
  ],
};

// DAY 3 — Upper Strength · Gym (INDOOR) · ALPHA + CHARLIE + DELTA + BRAVO
const trainingDay3: WorkoutTemplateDay = {
  name: 'Upper Strength + Engine',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    upperPrep,
    {
      name: 'Strength',
      type: 'strength',
      exercises: [
        { name: 'Bench Press', sets: 5, reps: 5, intensity: 'progressive', rest: '3 min', details: 'ALPHA — full lockout, controlled eccentric' },
        { name: 'Barbell Row (Pendlay)', sets: 4, reps: 6, intensity: 'progressive', rest: '2 min', details: 'ALPHA — from the floor, strict torso' },
        { name: 'Overhead Press', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'ALPHA — strict, no lean' },
      ],
    },
    {
      name: 'Bodyweight',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 4, reps: 6, intensity: 'progressive', rest: '90s', details: 'CHARLIE — full range; add weight when 6 is easy' },
        { name: 'Dip', sets: 3, reps: 'AMRAP', intensity: 'bodyweight', rest: '60s', details: 'CHARLIE — control the descent' },
      ],
    },
    {
      name: 'Loaded Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Suitcase Carry', sets: 4, reps: '40m', intensity: 'progressive', rest: '75s', details: 'DELTA — per side, anti-lateral flexion' },
      ],
    },
    {
      name: 'Z2 Engine',
      type: 'conditioning',
      exercises: [
        { name: 'Assault Bike', sets: 1, duration: '12 min', format: 'cardio', intensity: 'Z2', details: 'BRAVO — easy conversational pace. Bike or rower.' },
      ],
    },
    upperRecovery,
  ],
};

// DAY 4 — Ruck · OUTDOOR, self-contained · primary DELTA (+ CHARLIE).
// Everything here is doable on the trail: the ruck plus bodyweight that
// needs no equipment. No indoor machine bolted on — Bravo lives on the
// gym days + the aerobic day instead.
const trainingDay4: WorkoutTemplateDay = {
  name: 'Ruck',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    ruckPrep,
    {
      name: 'Ruck',
      type: 'conditioning',
      exercises: [
        { name: 'Ruck March', duration: '4 miles', format: 'ruck', intensity: 'progressive', details: 'DELTA — 35 lb (before water), steady pace over rolling terrain if you can find it' },
      ],
    },
    {
      // Superset not circuit — see the Day 2 note: circuits can't express an
      // AMRAP or a timed hold per member.
      name: 'Trailside Bodyweight',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', format: 'bodyweight', rest: '45s', details: 'CHARLIE — at the turnaround / finish, pack off' },
        { name: 'Air Squat', sets: 3, reps: 20, format: 'bodyweight', rest: '45s', details: 'CHARLIE — flush the legs' },
        { name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight', rest: '45s', details: 'CHARLIE — braced, no sag' },
      ],
    },
    ruckRecovery,
  ],
};

// DAY 5 — Power + Strength · Gym (INDOOR) · primary ALPHA (+ CHARLIE + DELTA).
// Sled push is a no-grip carry — deliberately relieves the grip that the
// deadlift/RDL + farmer/suitcase carries load on the other days.
const trainingDay5: WorkoutTemplateDay = {
  name: 'Power + Full-Body Strength',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    powerPrep,
    {
      name: 'Power Superset',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Front Squat', sets: 4, reps: 5, intensity: 'progressive', rest: '2 min', details: 'ALPHA — elbows high, depth' },
        { name: 'Push Press', sets: 4, reps: 5, intensity: 'progressive', rest: '2 min', details: 'ALPHA — drive with the legs, lock out overhead' },
      ],
    },
    {
      name: 'Strength',
      type: 'strength',
      exercises: [
        { name: 'Deadlift', sets: 3, reps: 5, intensity: 'progressive', rest: '3 min', details: 'ALPHA — conventional or trap bar, controlled' },
      ],
    },
    {
      name: 'Bodyweight',
      type: 'accessory',
      exercises: [
        { name: 'Pull-Up', sets: 4, reps: 6, intensity: 'progressive', rest: '90s', details: 'CHARLIE — full range; add weight when 6 is easy' },
      ],
    },
    {
      name: 'Loaded Push',
      type: 'conditioning',
      exercises: [
        { name: 'Sled Push', sets: 4, reps: '40m', intensity: 'progressive', rest: '90s', details: 'DELTA — low, driving steps (no grip demand)' },
      ],
    },
    powerRecovery,
  ],
};

// DAY 6 — Grinder · Gym (INDOOR) · all four sectors in one circuit.
// The week's high-intensity conditioning (covers the anaerobic end that the
// Z2 aerobic day deliberately doesn't).
const trainingDay6: WorkoutTemplateDay = {
  name: 'Four-Sector Grinder',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    grinderPrep,
    {
      name: 'Grinder Circuit',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Kettlebell Swing', sets: 5, reps: 20, intensity: 'progressive', format: 'circuit', details: 'ALPHA — explosive hip hinge. ALL FOUR SECTORS: 5 rounds, minimal rest.' },
        { name: 'Burpee', sets: 5, reps: 15, format: 'circuit', details: 'CHARLIE — chest to deck' },
        { name: 'Farmer Carry', sets: 5, reps: '40m', intensity: 'progressive', format: 'circuit', details: 'DELTA — heavy' },
        { name: 'Assault Bike', sets: 5, duration: '60s', format: 'circuit', details: 'BRAVO — hard 60s (bike or rower), then repeat' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hanging Leg Raise', sets: 3, reps: 12, intensity: 'progressive', rest: '45s', details: 'Controlled, no swing' },
        { name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight', rest: '45s', details: 'Braced' },
      ],
    },
    grinderRecovery,
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const taskforceSpearhead8Week: WorkoutProgram = {
  metadata,
  periodization: {
    style: 'block',
    phases: periodizationPhases,
  },
  templates: {
    'training-day1': trainingDay1,
    'training-day2': trainingDay2,
    'training-day3': trainingDay3,
    'training-day4': trainingDay4,
    'training-day5': trainingDay5,
    'training-day6': trainingDay6,
    'training-day7': restDay,
  },
  workouts: [],
};
